import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { currentLang, useT } from '../../i18n';
import { AppNotification, notificationText, relativeTime } from './notificationText';

/** Cada cuánto se revisa el contador mientras la pestaña está visible. */
const POLL_MS = 60_000;
const PAGE = 20;

const dotColor = (severity: AppNotification['severity']) => (severity === 'high' ? 'error.main' : severity === 'warning' ? 'warning.main' : 'info.main');

/**
 * Campana de avisos de la barra superior: contador de no leídos y lista de los últimos avisos.
 * Al tocar un aviso se marca como leído y, si lleva a una pantalla, se abre.
 */
const NotificationBell: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const lang = currentLang();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const mounted = useRef(true);

  const refreshCount = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    try {
      const { data } = await axios.get('notifications/unread-count');
      if (mounted.current) setUnread(Number(data?.unread) || 0);
    } catch {
      /* sin red o sin sesión: se intenta en la próxima revisión */
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refreshCount();
    const id = window.setInterval(() => void refreshCount(), POLL_MS);
    const onVisible = () => void refreshCount();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mounted.current = false;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshCount]);

  const load = async (before?: number) => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get('notifications', { params: { limit: PAGE, ...(before ? { before } : {}) } });
      const list: AppNotification[] = Array.isArray(data?.data) ? data.data : [];
      setItems((prev) => (before ? [...prev, ...list] : list));
      setHasMore(Boolean(data?.hasMore));
      setUnread(Number(data?.unread) || 0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const openList = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    void load();
  };

  const openItem = async (n: AppNotification) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      axios.post(`notifications/${n.id}/read`).catch(() => undefined);
    }
    if (n.link) {
      setAnchor(null);
      navigate(n.link);
    }
  };

  const markAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    axios.post('notifications/read-all').catch(() => undefined);
  };

  const label = unread ? `${t('notifications.open')} (${t('notifications.unread', { count: unread })})` : t('notifications.open');

  return (
    <>
      <Tooltip title={t('notifications.title')}>
        <IconButton color="inherit" aria-label={label} aria-haspopup="true" onClick={openList} data-tour="notifications">
          <Badge color="error" badgeContent={unread} max={99} overlap="circular">
            {unread ? <NotificationsOutlined /> : <NotificationsNoneOutlined />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 'min(420px, calc(100vw - 16px))', maxHeight: 'min(560px, calc(100dvh - 88px))', display: 'flex', flexDirection: 'column' } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25, minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {t('notifications.title')}
            </Typography>
            {unread > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('notifications.unread', { count: unread })}
              </Typography>
            )}
          </Box>
          {unread > 0 && (
            <Button size="small" onClick={markAll} sx={{ flexShrink: 0 }}>
              {t('notifications.markAllRead')}
            </Button>
          )}
          <Tooltip title={t('notifications.preferences')}>
            <IconButton
              size="small"
              aria-label={t('notifications.preferences')}
              onClick={() => {
                setAnchor(null);
                navigate('/notifications/preferences');
              }}
            >
              <SettingsOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Divider />
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {error && (
            <Typography sx={{ p: 2 }} color="error" variant="body2">
              {t('notifications.loadError')}
            </Typography>
          )}
          {!error && !loading && items.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsNoneOutlined sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                {t('notifications.empty')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('notifications.emptyHelp')}
              </Typography>
            </Box>
          )}
          {items.length > 0 && (
            <List disablePadding>
              {items.map((n) => {
                const text = notificationText(n, t);
                return (
                  <ListItemButton
                    key={n.id}
                    onClick={() => openItem(n)}
                    sx={{ alignItems: 'flex-start', gap: 1.25, py: 1.25, bgcolor: n.read ? 'transparent' : 'action.hover', borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.9, flexShrink: 0, bgcolor: n.read ? 'transparent' : dotColor(n.severity) }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={n.read ? 500 : 700} sx={{ overflowWrap: 'anywhere' }}>
                        {text.title}
                      </Typography>
                      {text.body && (
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          {text.body}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {relativeTime(n.createdAt, lang)}
                      </Typography>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {hasMore && !loading && (
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" onClick={() => load(items[items.length - 1]?.id)}>
                {t('notifications.loadMore')}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
